import { SubCard } from "./card/Card.tsx";
import type { EscrowFormValues } from "../pages/factory/CreateEscrow.tsx";
import type { Token } from "../repository/TokenWhitelistRepository.ts";
import { useEffect, useState } from "react";
import formatAmount from "../utils/millify.ts";
import { formatUnits, parseUnits } from "viem";
import { formatAddress } from "../utils/formatAddress.ts";
import { openExplorerInNewTab } from "../utils/explorerHelper.ts";

const Recap = ({
  data,
  onCreate,
  onGoBack,
  isAddPending,
}: {
  data: EscrowFormValues;
  onCreate: () => void;
  onGoBack: () => void;
  isAddPending: boolean;
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isAddPending) {
      setIsModalOpen(false);
    }
  }, [isAddPending]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const bigIntSellerWIllReceiveAmount = parseUnits(
    data.buyerTradeAmount.toString(),
    data.buyerTradeToken.decimals,
  );

  const bigIntSellerDepositAmount = parseUnits(
    data.sellerDepositAmount.toString(),
    data.sellerDepositToken.decimals,
  );

  const bigIntSellerFeesAmount = parseUnits(
    data.sellerFeesAmount.toString(),
    data.sellerFeesToken.decimals,
  );

  const bigIntSellerTradeAmount = parseUnits(
    data.sellerTradeAmount.toString(),
    data.sellerTradeToken.decimals,
  );

  const bigIntBuyerWIllReceiveAmount = parseUnits(
    data.sellerTradeAmount.toString(),
    data.sellerTradeToken.decimals,
  );

  const bigIntBuyerDepositAmount = parseUnits(
    data.buyerDepositAmount.toString(),
    data.buyerDepositToken.decimals,
  );

  const bigIntBuyerFeesAmount = parseUnits(
    data.buyerFeesAmount.toString(),
    data.buyerFeesToken.decimals,
  );

  const bigIntBuyerTradeAmount = parseUnits(
    data.buyerTradeAmount.toString(),
    data.buyerTradeToken.decimals,
  );

  return (
    <>
      <SubCard title={"Seller"}>
        <PartyRecapTable
          nickname={null}
          isLoading={false}
          address={data.sellerAddress}
          willReceiveToken={data.buyerTradeToken}
          willReceiveAmount={bigIntSellerWIllReceiveAmount}
          tradeToken={data.sellerTradeToken}
          tradeAmount={bigIntSellerTradeAmount}
          depositToken={data.sellerDepositToken}
          depositAmount={bigIntSellerDepositAmount}
          feeToken={data.sellerFeesToken}
          feeAmount={bigIntSellerFeesAmount}
        />
      </SubCard>
      <SubCard title={"Buyer"}>
        <PartyRecapTable
          nickname={null}
          isLoading={false}
          address={data.buyerAddress}
          willReceiveToken={data.sellerTradeToken}
          willReceiveAmount={bigIntBuyerWIllReceiveAmount}
          tradeToken={data.buyerTradeToken}
          tradeAmount={bigIntBuyerTradeAmount}
          depositToken={data.buyerDepositToken}
          depositAmount={bigIntBuyerDepositAmount}
          feeToken={data.buyerFeesToken}
          feeAmount={bigIntBuyerFeesAmount}
        />
      </SubCard>
      <SubCard title={"Exchange settings"}>
        <SettingsRecapTable duration={data.duration} />
        <div>
          <div className="card-actions justify-end mt-4">
            <button
              className="btn btn-error text-white"
              onClick={onGoBack}
              disabled={isAddPending}
            >
              Back
            </button>
            <button
              className="btn btn-primary"
              onClick={handleOpenModal}
              disabled={isAddPending}
            >
              Continue
            </button>
          </div>
        </div>
        <ConfirmationModal
          isOpen={isModalOpen}
          onConfirm={onCreate}
          onClose={handleCloseModal}
        />
      </SubCard>
    </>
  );
};

const SettingsRecapTable = ({ duration }: { duration: number }) => {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Setting</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Exchange duration</td>
          <td className={"flex items-center gap-2"}>{duration} hours</td>
        </tr>
      </tbody>
    </table>
  );
};

const PartyRecapTable = ({
  nickname,
  isLoading = false,
  address,
  willReceiveAmount,
  willReceiveToken,
  tradeToken,
  tradeAmount,
  depositAmount,
  depositToken,
  feeToken,
  feeAmount,
}: {
  nickname: string | null;
  isLoading: boolean;
  address: string;
  willReceiveToken: Token;
  willReceiveAmount: bigint;
  tradeToken: Token;
  tradeAmount: bigint;
  depositToken: Token;
  depositAmount: bigint;
  feeToken: Token;
  feeAmount: bigint;
}) => {
  return (
    <table className="table table-fixed">
      <thead>
        <tr>
          <th>Address</th>
          <th>Will receive</th>
          <th>In exchange for</th>
          <th>Penalty clause</th>
          <th>Fees</th>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td>
              <div className="skeleton h-6 w-48" />
            </td>
            <td>
              <div className="skeleton h-6 w-48" />
            </td>
            <td>
              <div className="skeleton h-6 w-48" />
            </td>
            <td>
              <div className="skeleton h-6 w-48" />
            </td>
          </tr>
        ) : (
          <tr key={address}>
            <td
              className={"cursor-pointer"}
              onClick={() => openExplorerInNewTab(address)}
            >
              {formatAddress(address, true)} {nickname && `(${nickname})`}
            </td>
            <td>
              <div className="flex items-center gap-2">
                {formatAmount(
                  formatUnits(willReceiveAmount, willReceiveToken.decimals),
                  { precision: 10 },
                )}
                <div className="badge badge-neutral badge-sm">
                  {willReceiveToken.symbol}
                </div>
              </div>
            </td>
            <td>
              <div className="flex items-center gap-2">
                {formatAmount(formatUnits(tradeAmount, tradeToken.decimals), {
                  precision: 10,
                })}
                <div className="badge badge-neutral badge-sm">
                  {tradeToken.symbol}
                </div>
              </div>
            </td>
            <td>
              <div className="flex items-center gap-2">
                {formatAmount(
                  formatUnits(depositAmount, depositToken.decimals),
                  { precision: 10 },
                )}
                <div className="badge badge-neutral badge-sm">
                  {depositToken.symbol}
                </div>
              </div>
            </td>
            <td>
              <div className="flex items-center gap-2">
                {formatAmount(formatUnits(feeAmount, feeToken.decimals), {
                  precision: 10,
                })}
                <div className="badge badge-neutral badge-sm">
                  {feeToken.symbol}
                </div>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box">
        <h3 className="font-bold text-lg">Confirm Escrow Contract Creation</h3>
        <p className="py-4">Are you sure you want to create this contract?</p>
        <div className="modal-action">
          <button className="btn btn-error text-white" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export { Recap, PartyRecapTable };
